import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import whiteArrow from "../../assets/svg/white-arrow.svg";
import locationSvg from "../../assets/svg/location-white.svg";
import addSvg from "../../assets/svg/plus-white.svg";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { LoginAndSignupButtons } from "./LoginAndSignupButtons";
import { countryParser, IsRunningOverwolf } from "../../utilityFunctions";
import { InGameAppDownloadButton } from "components/promo/InGameAppDownloadButton";
import { useWindowSize } from "hooks/useWindowSize";
import { NotificationBell } from "./notificationBell/NotificationBell";
import { SET_DEMO_ADDRESS } from "store/actions/actionTypes/types";
import { NotificationBanner } from "components/NotificationBanner";
import logoWithoutBackground from "../../assets/images/liifer-logo-no-tm.png";
import useApplicationInfo from "hooks/useAppInfo";
import { StatusBarBackground } from "components/StatusBarBackground";
import { useGlobalState } from "GlobalStateContext";
import { HashLink } from "react-router-hash-link";
import { Modal } from "components/dashboard/Modal";
import { ResponseOption } from "components/ResponseOption";
import { CommentForm } from "components/CommentForm";
import { SuccessToast } from "components/dashboard/SuccessToast";
import { LanguageSelector } from "components/LanguageSelector";
import ToggleDelivery from "components/ToggleDelivery";

export const Header = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const { application } = useApplicationInfo();
  const isRunningOverwolf = IsRunningOverwolf();
  const isRunningInGame = application === "OW_INGAME_CLIENT";
  const isRunningIos = application === "IOS_CLIENT";
  const isRunningCapacitor = application === "ANDROID_CLIENT" || isRunningIos;
  const userInfo = useAppSelector((state) => state.auth.userInfo);
  const userMeals = useAppSelector((state) => state.meals.userMeals);
  const accessToken = useAppSelector((state) => state.auth.accessToken);
  const demoAddress = useAppSelector((state) => state.auth.demoAddress);
  const [width] = useWindowSize();
  const { setAccountPageIndex } = useGlobalState();
  const { setMobileLandingIndex } = useGlobalState();
  const navigationIsDisabled = useAppSelector(
    (state) => state.meals.navigationIsDisabled
  );

  const [addressDescription, setAddressDescription] = useState<string>("");

  useEffect(() => {
    if (userInfo?.address) {
      const {
        street = "",
        city = "",
        state = "",
        zip = "",
        country = "",
      } = userInfo.address;
      const newAddressDescription = `${street}, ${city}, ${state}, ${zip}, ${countryParser(
        country
      )}`;

      setAddressDescription(newAddressDescription.trim());
    }
  }, [
    userInfo?.address,
    userInfo?.address?.street,
    userInfo?.address?.city,
    userInfo?.address?.state,
    userInfo?.address?.zip,
    userInfo?.address?.country,
  ]);

  const onLandingPage = pathname.slice(0, 5) === "/home";
  const onPartnersPage = pathname.slice(0, 14) === "/partners";
  const onAccountPage = pathname.slice(0, 8) === "/account";
  const onSocialMediaProgramPage =
    pathname.slice(0, 19) === "/socialMediaProgram";
  const onMealsPage = pathname.slice(0, 18) === "/dashboard/mymeals";
  const onMealCreatorPage = pathname.slice(0, 17) === "/dashboard/create";
  const onRestListPage = pathname.slice(0, 19) === "/dashboard/restlist";
  const onNearbyPage = pathname.slice(0, 7) === "/nearby";
  const onCheckOutPage = pathname.slice(0, 19) === "/dashboard/checkout";
  const usingCoordinatesForDemoSearch = pathname.slice(0, 7) === "/nearby";
  const onOrderHistoryPage =
    pathname.slice(0, 23) === "/dashboard/orderhistory";
  const onFAQPage = pathname.slice(0, 4) === "/FAQ";
  const onSocialSignupPage = pathname.slice(0, 13) === "/socialSignup";

  const REACT_APP_STORE_MAINTENANCE_MODE = useAppSelector(
    (state) => state?.auth?.env?.REACT_APP_STORE_MAINTENANCE_MODE
  );
  const showMaintenanceWarning =
    REACT_APP_STORE_MAINTENANCE_MODE === "true" &&
    (onAccountPage || onMealsPage || onRestListPage || onMealCreatorPage);

  useEffect(() => {
    if (usingCoordinatesForDemoSearch && !!demoAddress) {
      dispatch({ type: SET_DEMO_ADDRESS, payload: null });
      sessionStorage.removeItem("demoAddress");
    }
  });

  const hideHeader: boolean =
    width < 768 &&
    userInfo?.anonymousMode &&
    !onAccountPage &&
    !onLandingPage &&
    !onSocialSignupPage;

  const hideAnonymousHeader: boolean =
    userInfo?.anonymousMode &&
    (pathname.slice(0, 18) === "/dashboard/mymeals" ||
      pathname.slice(0, 23) === "/dashboard/orderhistory" ||
      pathname.slice(0, 17) === "/dashboard/create" ||
      pathname.slice(0, 19) === "/dashboard/restlist") &&
    isRunningOverwolf;

  const statusBarHeight = useAppSelector((state) => state.info.statusBarHeight);

  const shouldRenderBackToMeals =
    !!accessToken && !(width < 768) && (onLandingPage || onFAQPage);

  const [showModal, setShowModal] = useState<boolean>(false);
  const [showResponseOption, setShowResponseOption] = useState<boolean>(true);
  const [showToast, setShowToast] = useState<boolean>(false);

  const onCompletedModal = () => {
    setShowToast(true);
    closeModal();
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const showCommentForm = () => {
    setShowResponseOption(false);
  };

  const showResponseComponent = () => {
    setShowResponseOption(true);
  };

  return (
    <div
      className={`relative ${
        isRunningOverwolf ? "-mt-2 lg:-mt-3 lg:pb-2" : ""
      }`}
    >
      {/* Gradient overlay */}
      {!accessToken &&
        !onRestListPage &&
        !onNearbyPage &&
        !isRunningCapacitor && (
          <div
            className="pointer-events-none absolute inset-x-0 top-0 h-52"
            style={{
              background:
                "linear-gradient(to bottom, rgba(0, 0, 0, 1), rgba(0, 0, 0, 0))",
            }}
          />
        )}

      {isRunningOverwolf && !!accessToken && (
        <div
          className={` ${
            isRunningInGame ? "right-28 z-50" : "right-48 z-50"
          } fixed top-1 mt-0.5`}
        >
          <NotificationBell />
        </div>
      )}
      <div className={` ${hideAnonymousHeader ? "mt-8 2xl:mt-12" : ""}`}>
        {!!statusBarHeight && <StatusBarBackground />}
        <div className={`${hideAnonymousHeader ? "hidden" : "block"}`}>
          {!hideHeader && !onCheckOutPage ? (
            <div
              className={`${
                !accessToken && !onRestListPage ? "items-center" : ""
              }`}
            >
              <header
                id="header"
                className={`flex items-center ${
                  !accessToken && !onRestListPage
                    ? "w-full xl:px-14 2xl:px-20"
                    : "max-w-full"
                } ${
                  (onMealsPage ||
                    (onRestListPage && !!accessToken) ||
                    onSocialMediaProgramPage ||
                    onCheckOutPage ||
                    onOrderHistoryPage) &&
                  width < 768 &&
                  isRunningOverwolf
                    ? "justify-center"
                    : "justify-between"
                } ${
                  isRunningOverwolf
                    ? "pt-10 md:pt-12 lg:pt-16 2xl:mt-2"
                    : "pt-4 md:pt-6 lg:pt-12 2xl:pt-14 lg:mb-4"
                } p-4 px-6 lg:px-8 2xl:px-14 h-20 lg:h-14`}
              >
                <div
                  className={`flex items-center ${
                    !accessToken && !onRestListPage && !isRunningOverwolf
                      ? "justify-center w-full"
                      : isRunningOverwolf
                      ? "w-full"
                      : onRestListPage && accessToken && width < 767
                      ? "w-full"
                      : "w-auto"
                  }`}
                >
                  {!!accessToken &&
                    !isRunningOverwolf &&
                    (onLandingPage || width > 767) && (
                      <div>
                        <div>
                          <div
                            style={{ backgroundColor: "#1D283E" }}
                            className="flex flex-row rounded-2xl"
                          >
                            <Link
                              to="/dashboard/mymeals"
                              className="hover:opacity-70 transition ease-in-out duration-200"
                              draggable={false}
                            >
                              <img
                                className="h-8 m-3 mx-4"
                                src={logoWithoutBackground}
                                alt="menu"
                              />
                            </Link>
                          </div>
                        </div>
                      </div>
                    )}
                  <div
                    className={`flex h-full items-center ${
                      !accessToken && !onRestListPage
                        ? "w-full justify-between"
                        : "w-full md:w-auto"
                    }`}
                  >
                    {!accessToken && !isRunningOverwolf && (
                      <Link
                        className={` ${
                          navigationIsDisabled ? "pointer-events-none" : ""
                        }`}
                        to={(() => {
                          if (isRunningCapacitor) {
                            setMobileLandingIndex(4);
                            return "/home";
                          }
                          return !!accessToken ? "/dashboard/mymeals" : "/home";
                        })()}
                        draggable={false}
                      >
                        <div
                          style={{
                            width:
                              !onRestListPage && width >= 1024
                                ? "260px"
                                : "135px",
                          }}
                          className="flex flex-row rounded-full"
                        >
                          <Link
                            to="/home"
                            className="hover:opacity-70 transform ease-in-out duration-200"
                            draggable={false}
                          >
                            <img
                              className="h-8 md:h-9"
                              src={logoWithoutBackground}
                              alt="menu"
                            />
                          </Link>
                        </div>
                      </Link>
                    )}
                    {!isRunningCapacitor && (
                      <div
                        className={`text-slate-100 flex ${
                          isRunningOverwolf ? "justify-start" : "justify-center"
                        } space-x-8 xl:space-x-12 items-center ${
                          onRestListPage && accessToken && width < 767
                            ? "hidden"
                            : ""
                        } w-3/5 2xl:w-2/5`}
                      >
                        {isRunningOverwolf && width < 1024 && !accessToken && (
                          <Link
                            to="/home"
                            className={`hover:opacity-70 transform ease-in-out duration-200 px-4 py-1 ${
                              onLandingPage
                                ? "bg-slate-700 bg-opacity-60 rounded-lg"
                                : ""
                            }`}
                            draggable={false}
                          >
                            <h1>{t("header.home")}</h1>
                          </Link>
                        )}
                        <div
                          className={` ${
                            accessToken || onRestListPage
                              ? "hidden"
                              : "hidden lg:flex"
                          } justify-center items-center space-x-4 xl:space-x-6 2xl:space-x-10`}
                        >
                          <Link
                            to="/home"
                            className={`hover:opacity-70 transform ease-in-out duration-200 px-4 py-1 ${
                              onLandingPage
                                ? "bg-slate-700 bg-opacity-60 rounded-lg"
                                : ""
                            }`}
                            draggable={false}
                          >
                            <h1>{t("header.home")}</h1>
                          </Link>
                          <Link
                            to="/partners"
                            className={`hover:opacity-70 transform ease-in-out duration-200 px-4 py-1 ${
                              onPartnersPage
                                ? "bg-slate-700 bg-opacity-60 rounded-lg"
                                : ""
                            }`}
                            draggable={false}
                          >
                            <h1>{t("header.parterWithUs")}</h1>
                          </Link>
                          {!isRunningOverwolf && (
                            <a
                              href="https://www.overwolf.com/app/liifer_technologies_inc-liifer_application"
                              className="hover:opacity-70 transform ease-in-out duration-200 px-2 py-1"
                              draggable={false}
                              target="_blank"
                              rel="noreferrer"
                            >
                              <h1>{t("header.liiferOnOverwolf")}</h1>
                            </a>
                          )}
                          <button
                            className="hover:opacity-70 transform ease-in-out duration-200 px-2 py-1 hidden xl:block"
                            onClick={() => setShowModal(true)}
                          >
                            <p className="text-left">{t("footer.support")}</p>
                          </button>
                        </div>
                      </div>
                    )}
                    {!userInfo && !onRestListPage && (
                      <div className="z-20">
                        <div className="flex justify-end items-center">
                          {isRunningOverwolf && width > 767 && (
                            <LanguageSelector />
                          )}
                          <LoginAndSignupButtons />
                        </div>
                      </div>
                    )}
                    {(onMealsPage ||
                      onRestListPage ||
                      (onMealCreatorPage && width > 767) ||
                      onOrderHistoryPage) &&
                      (!!accessToken || width > 767) &&
                      !userInfo?.anonymousMode && (
                        <div className="flex justify-between items-center w-full">
                          <HashLink
                            to={
                              !!accessToken
                                ? "/account/info"
                                : "/dashboard/restlist"
                            }
                            className={`${
                              !!accessToken
                                ? "hover:opacity-70 transform ease-in-out duration-200"
                                : "cursor-default"
                            } ${
                              addressDescription === "" && accessToken
                                ? "hidden"
                                : ""
                            }`}
                            draggable={false}
                            onClick={() => {
                              !!accessToken && setAccountPageIndex(1);
                            }}
                          >
                            <div
                              className={`flex justify-center h-12 md:ml-2 ${
                                onRestListPage && accessToken
                                  ? "w-[45vw] sm:w-[60vw]"
                                  : "w-[75vw] sm:w-full"
                              } md:w-64 lg:w-88 xl:w-96 2xl:w-full bg-slate-800 text-slate-200 md:mx-auto rounded-2xl ${
                                !isRunningOverwolf
                                  ? "md:pl-1 lg:pl-2 -ml-2"
                                  : "-ml-4"
                              } py-1 shadow-lg`}
                            >
                              <div className="flex items-center justify-center space-x-2 px-2 md:px-4 overflow-hidden">
                                <img
                                  className="h-3 px-1"
                                  src={locationSvg}
                                  alt="location"
                                />
                                <span className="truncate whitespace-nowrap text-center text-sm 2xl:text-base">
                                  {!!accessToken
                                    ? addressDescription
                                    : demoAddress?.description}
                                </span>
                              </div>
                            </div>
                          </HashLink>
                        </div>
                      )}
                    {onRestListPage && accessToken && (
                      <div className="absolute right-2 sm:right-6 2xl:right-12 3xl:right-16">
                        <ToggleDelivery />
                      </div>
                    )}
                  </div>
                  {shouldRenderBackToMeals && (
                    <div className="absolute left-2 md:relative">
                      <Link to="/dashboard/mymeals" draggable={false}>
                        <button className="flex items-center justify-center bg-[#EB112E] text-gray-200 text-xs lg:text-sm px-3 py-2 rounded-full ml-2 lg:ml-4 hover:bg-opacity-70 transition duration-300 ease-in-out">
                          <span className="whitespace-nowrap">
                            {t("menu.backToMyMeals")}
                          </span>
                          <img
                            className="h-3 pl-1 m-auto"
                            src={whiteArrow}
                            alt="arrow"
                          />
                        </button>
                      </Link>
                    </div>
                  )}
                </div>
                <div
                  className={`${
                    !onMealCreatorPage &&
                    !onRestListPage &&
                    !onMealsPage &&
                    !onOrderHistoryPage
                      ? "space-x-4"
                      : ""
                  } md:space-x-1 flex items-center justify-center`}
                >
                  <div className="flex justify-center space-x-2 items-center lg:pl-1 text-slate-200">
                    {!userInfo && onRestListPage && (
                      <>
                        <div className="flex items-center">
                          <LoginAndSignupButtons />
                        </div>
                      </>
                    )}
                    {!!userInfo?.address &&
                      pathname.slice(0, 18) === "/dashboard/mymeals" &&
                      userMeals?.length < 10 && (
                        <div className="absolute md:relative right-2 md:right-auto">
                          <Link to="/dashboard/restlist" draggable={false}>
                            <div className="hidden md:block lg:hidden mr-1 3xl:mr-4">
                              <button>
                                <div className="flex justify-center items-center space-x-2 bg-blue-600 hover:bg-opacity-70 transition ease-in-out duration-200 text-white rounded-2xl p-3">
                                  <img className="w-5" src={addSvg} alt="add" />
                                </div>
                              </button>
                            </div>
                            <div className="hidden lg:block mr-1 3xl:mr-4">
                              <button className="w-36">
                                <div className="flex justify-center items-center space-x-2 bg-blue-600 hover:bg-opacity-70 transition ease-in-out duration-200 text-white rounded-2xl pl-5 pr-6 py-3">
                                  <img className="w-5" src={addSvg} alt="add" />
                                  <span>{t("meals.addMeal")}</span>
                                </div>
                              </button>
                            </div>
                          </Link>
                        </div>
                      )}
                    {onMealsPage && !isRunningOverwolf && !isRunningIos && (
                      <div className="hidden lg:block 3xl:pr-4">
                        <InGameAppDownloadButton hideLabel />
                      </div>
                    )}
                  </div>
                  {!!accessToken && (
                    <div
                      className={`${
                        onCheckOutPage ? "pl-2 md:pt-9 lg:pt-6" : ""
                      } ${
                        onCheckOutPage ? "absolute right-64" : ""
                      } pl-2 md:pt-4 lg:pt-2`}
                    >
                      {!onCheckOutPage &&
                        !onMealCreatorPage &&
                        !onRestListPage &&
                        !isRunningOverwolf && (
                          <div className="mt-4 sm:mt-5 -ml-4 md:-ml-0 md:mt-0 sm:absolute sm:right-6 sm:top-4 md:relative md:top-0 md:right-0 w-12">
                            <NotificationBell />
                          </div>
                        )}
                    </div>
                  )}
                </div>
              </header>
              {showModal && (
                <Modal
                  show={showModal}
                  handleClose={closeModal}
                  title={t("comment.title")}
                >
                  {showResponseOption ? (
                    <ResponseOption
                      setShowResponseOptionModal={setShowModal}
                      onShowCommentForm={showCommentForm}
                    />
                  ) : (
                    <CommentForm
                      onCompleted={onCompletedModal}
                      setShowCommentModal={setShowModal}
                      onshowResponseComponent={showResponseComponent}
                    />
                  )}
                </Modal>
              )}
              <SuccessToast
                show={showToast}
                handleClose={() => setShowToast(false)}
                message={
                  <span className="text-slate-100 text-base space-x-2 flex font-bold">
                    {t("comment.success")}
                  </span>
                }
              />
              {showMaintenanceWarning && (
                <div
                  className={`px-4 lg:px-0 ${
                    onMealCreatorPage
                      ? "w-full lg:w-2/3 xl:w-1/3 lg:ml-4"
                      : "md:w-2/3 lg:pr-14 2xl:pr-20 lg:pl-6"
                  }  justify-center items-center pt-1`}
                >
                  <NotificationBanner message={t("maintenance.warning")} />
                </div>
              )}
            </div>
          ) : (
            <div
              className={`${
                showMaintenanceWarning ? "py-4" : !onCheckOutPage ? "h-6" : ""
              }`}
            >
              {showMaintenanceWarning && (
                <div
                  className={`px-4 lg:px-0 ${
                    onMealCreatorPage
                      ? "w-full lg:w-2/3 xl:w-1/3 lg:ml-4"
                      : "md:w-2/3 lg:pr-14 2xl:pr-20 lg:pl-6"
                  }  justify-between items-center pt-1`}
                >
                  <NotificationBanner message={t("maintenance.warning")} />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Header;
